import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import FlowAssigneePicker from './FlowAssigneePicker';
import { FLOW_CHANGED_EVENT } from './flowEvents';
import { Share2 } from 'lucide-react';
import { toast } from 'react-toastify';
import { useCustomDispatch, useCustomNavigate } from '../../../../hooks';
import { useCustomSelectors } from '../../../../services/selectors/allSelectors';
import { initialPharmacyDatePicker } from '../../../../assets/js/datepicker';
import { APP_TIMEZONE } from '../../../../utils/constants/formInitialStates';
import {
	CreateDailyRoutineTemplateItem,
	DeleteDailyRoutineTemplateItem,
	FetchDailyRoutineStaff,
	FetchDailyRoutineTemplate,
	FetchDailyRoutineToday,
	FetchSharedDailyRoutineAvailability,
	FetchSharedDailyRoutineTemplate,
	FetchSharedDailyRoutineTemplates,
	ImportSharedDailyRoutineTemplate,
	LookupDailyRoutineTemplateItem,
	ReorderDailyRoutineTemplateItems,
	ToggleDailyRoutineRunItem,
	UpdateDailyRoutineTemplateItem
} from '../../../../services/reducers/pharmacy/accountibility/accountibilityNewRx';
import {
	getDailyRoutineRouteOptions,
	normalizeDailyRoutineSearch
} from './dailyRoutineRoutes';

const WEEK_DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const WEEK_DAY_SHORT = {
	Sunday: 'S',
	Monday: 'M',
	Tuesday: 'T',
	Wednesday: 'W',
	Thursday: 'T',
	Friday: 'F',
	Saturday: 'S'
};
const API_DAY_BY_LABEL = {
	Sunday: 'sun',
	Monday: 'mon',
	Tuesday: 'tue',
	Wednesday: 'wed',
	Thursday: 'thu',
	Friday: 'fri',
	Saturday: 'sat'
};
const LABEL_BY_API_DAY = {
	sun: 'Sunday',
	sunday: 'Sunday',
	mon: 'Monday',
	monday: 'Monday',
	tue: 'Tuesday',
	tues: 'Tuesday',
	tuesday: 'Tuesday',
	wed: 'Wednesday',
	wednesday: 'Wednesday',
	thu: 'Thursday',
	thur: 'Thursday',
	thurs: 'Thursday',
	thursday: 'Thursday',
	fri: 'Friday',
	friday: 'Friday',
	sat: 'Saturday',
	saturday: 'Saturday'
};

const normalizeRecurrenceType = (value) => {
	if (value === 'weekly_days' || value === 'weekly') return 'weekly';
	if (value === 'interval' || value === 'custom') return 'custom';
	if (value === 'daily') return 'daily';
	return null;
};

const getTodayInAppTimezone = () => {
	const parts = new Intl.DateTimeFormat('en-CA', {
		timeZone: APP_TIMEZONE,
		year: 'numeric',
		month: '2-digit',
		day: '2-digit'
	}).formatToParts(new Date());
	const values = Object.fromEntries(parts.map(({ type, value }) => [type, value]));
	return `${values.year}-${values.month}-${values.day}`;
};

const normalizeWeekDays = (value) => {
	const list = Array.isArray(value)
		? value
		: typeof value === 'string'
			? value.split(',')
			: [];

	return list
		.map((day) => LABEL_BY_API_DAY[String(day).trim().toLowerCase()])
		.filter(Boolean);
};

const getTaskRoute = (task) => task?.route || task?.description || '';
const getTaskHighlightKey = (task) => `${normalizeDailyRoutineSearch(task?.label || '')}|${getTaskRoute(task) || ''}`;

const DailyRoutineDatePicker = ({ value, disabled, onChange }) => {
	const datePickerRef = useRef(null);
	const valueRef = useRef(value);
	const onChangeRef = useRef(onChange);

	valueRef.current = value;
	onChangeRef.current = onChange;

	const handleDateChange = useCallback((updater) => {
		const nextValue = typeof updater === 'function'
			? updater({ recurrenceAnchorDate: valueRef.current })
			: updater;
		onChangeRef.current?.(nextValue?.recurrenceAnchorDate || '');
	}, []);

	useEffect(() => {
		const datePicker = initialPharmacyDatePicker(
			datePickerRef,
			handleDateChange,
			'recurrenceAnchorDate',
			''
		);

		return () => datePicker?.destroy();
	}, [handleDateChange]);

	return (
		<input
			type="text"
			ref={datePickerRef}
			value={value || ''}
			disabled={disabled}
			readOnly
			aria-label="Custom recurrence start date"
			className="h-9 w-full rounded-md border border-gray-300 px-3 text-sm leading-normal text-gray-700 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 disabled:opacity-60"
		/>
	);
};

const isTaskConfigured = (task) => {
	if (task?.recurrenceType === 'daily') return true;
	if (task?.recurrenceType === 'weekly' && Array.isArray(task?.weeklyDays) && task.weeklyDays.length > 0) {
		return true;
	}
	if (task?.recurrenceType === 'custom') {
		const intervalValue = Number(task?.intervalValue);
		return Number.isInteger(intervalValue) &&
			intervalValue >= 1 &&
			intervalValue <= 365 &&
			['day', 'week', 'month'].includes(task?.intervalUnit) &&
			Boolean(task?.recurrenceAnchorDate);
	}
	return false;
};

const extractPayloadData = (response) => response?.data ?? response ?? {};

const extractTodayRun = (response) => {
	const payload = extractPayloadData(response);

	return payload?.data?.run ||
		payload?.data?.today_run ||
		payload?.run ||
		payload?.today_run ||
		null;
};

const extractTemplate = (response) => {
	const payload = extractPayloadData(response);
	return payload?.data?.template || payload?.template || null;
};

const extractSharedTemplates = (response) => {
	const payload = extractPayloadData(response);
	const templates = payload?.data?.templates || payload?.templates || [];
	return Array.isArray(templates)
		? templates.filter((template) => Number(template?.items_count ?? template?.items?.length ?? 0) > 0)
		: [];
};

const extractSharedAvailability = (response) => {
	const payload = extractPayloadData(response);
	return Boolean(payload?.data?.has_available_workflows ?? payload?.has_available_workflows);
};

const getSharedFlowLabel = (template) => {
	const username = template?.created_by?.name?.trim() || '';
	return `${username} Flow`;
};

const extractLookupData = (response) => {
	const payload = extractPayloadData(response);
	return payload?.data || payload || {};
};

const extractToggleItem = (response) => {
	const payload = extractPayloadData(response);
	return payload?.data?.item || payload?.item || null;
};

// A step someone assigned to this user shows "New" for this long, unless it's already ticked.
const NEW_ASSIGNMENT_MS = 24 * 60 * 60 * 1000;
const isNewAssignment = (task, now = Date.now()) => {
	if (!task?.isAssignedCopy || !task?.assignedAt || task?.isSelected) return false;
	const assignedAt = Date.parse(task.assignedAt);
	return Number.isFinite(assignedAt) && now - assignedAt < NEW_ASSIGNMENT_MS;
};

// Staff names are stored as typed (e.g. "umang k mishra"); show each word capitalized.
const capitalizeName = (name) => (name || '').replace(/\S+/g, (word) => word.charAt(0).toUpperCase() + word.slice(1));

const extractStaff = (response) => {
	const payload = extractPayloadData(response);
	return payload?.data?.staff || payload?.staff || [];
};

// Assignees of a flow step: [{ id, name }]. `null` means "not known yet" (run items don't carry them).
const normalizeAssignees = (value) => (
	Array.isArray(value)
		? value
			.filter((user) => user?.id != null)
			.map((user) => ({ id: Number(user.id), name: user?.name || '' }))
		: null
);

const getAssignedCopyMessage = (task) => (
	`This step was assigned to you${task?.assignedBy?.name ? ` by ${task.assignedBy.name}` : ''} and can only be changed by them.`
);

const extractTemplateOrderIdsFromRun = (response) => {
	const run = extractTodayRun(response);
	if (!Array.isArray(run?.items)) return [];

	return run.items
		.map((item) => item?.template_item_id ?? item?.templateId ?? null)
		.filter((value) => value != null);
};

const extractValidationErrors = (error) => {
	const errors = error?.response?.data?.errors;
	if (!errors || typeof errors !== 'object') return '';

	return Object.values(errors)
		.flat()
		.filter(Boolean)
		.join(' ');
};

const getErrorMessage = (error, fallback) => {
	return extractValidationErrors(error) ||
		error?.response?.data?.message ||
		error?.response?.message ||
		error?.message ||
		fallback;
};

const createDraftTask = ({ title, route = '' }) => ({
	id: `draft-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
	templateId: null,
	runItemId: null,
	label: title,
	description: route,
	workflowDescription: '',
	route,
	isSelected: false,
	recurrenceType: null,
	weeklyDays: [],
	intervalValue: 1,
	intervalUnit: 'day',
	recurrenceAnchorDate: getTodayInAppTimezone(),
	sortOrder: Number.MAX_SAFE_INTEGER,
	isApplicableToday: false,
	isDraft: true,
	isAssignedCopy: false,
	assignedBy: null,
	assignees: []
});

const mapRunItemToTask = (item) => ({
	id: item?.template_item_id ? `template-${item.template_item_id}` : `run-${item?.id}`,
	templateId: item?.template_item_id ?? null,
	runItemId: item?.id ?? null,
	label: item?.title ?? '',
	description: item?.description ?? '',
	workflowDescription: item?.workflow_description ?? '',
	route: item?.description ?? '',
	isSelected: Boolean(item?.is_completed),
	recurrenceType: normalizeRecurrenceType(item?.recurrence_type),
	weeklyDays: normalizeWeekDays(item?.days_of_week),
	intervalValue: item?.interval_value ?? 1,
	intervalUnit: item?.interval_unit ?? 'day',
	recurrenceAnchorDate: item?.recurrence_anchor_date ?? getTodayInAppTimezone(),
	sortOrder: item?.sort_order ?? 0,
	isApplicableToday: true,
	isDraft: false,
	isAssignedCopy: Boolean(item?.is_assigned_copy),
	assignedBy: item?.assigned_by ?? null,
	assignedAt: item?.assigned_at ?? null,
	completedAtLabel: item?.completed_at_label ?? null,
	completedBy: item?.completed_by ?? null,
	assignees: normalizeAssignees(item?.assignees)
});

const mapTemplateItemToTask = (item, today = {}, fallbackRoute = '') => {
	const route = item?.description ?? fallbackRoute;
	const templateId = item?.id ?? item?.template_item_id ?? null;

	return {
		id: templateId ? `template-${templateId}` : `template-${Date.now()}`,
		templateId,
		runItemId: today?.run_item_id ?? null,
		label: item?.title ?? '',
		description: route ?? '',
		workflowDescription: item?.workflow_description ?? '',
		route: route ?? '',
		isSelected: Boolean(today?.is_completed),
		recurrenceType: normalizeRecurrenceType(item?.recurrence_type),
		weeklyDays: normalizeWeekDays(item?.days_of_week),
		intervalValue: item?.interval_value ?? 1,
		intervalUnit: item?.interval_unit ?? 'day',
		recurrenceAnchorDate: item?.recurrence_anchor_date ?? getTodayInAppTimezone(),
		sortOrder: item?.sort_order ?? 0,
		isApplicableToday: Boolean(today?.is_applicable),
		isDraft: false,
		isAssignedCopy: Boolean(item?.is_assigned_copy),
		assignedBy: item?.assigned_by ?? null,
		completedAtLabel: today?.completed_at_label ?? null,
		completedBy: today?.completed_by ?? null,
		assignees: normalizeAssignees(item?.assignees)
	};
};

const buildTemplatePayload = (task) => {
	const title = task?.label?.trim();
	if (!title) return null;

	// Only send assignees when they are known, so an unknown list never wipes existing assignees.
	const assigneePayload = Array.isArray(task?.assignees)
		? { assignee_ids: task.assignees.map((user) => Number(user.id)) }
		: {};

	if (task?.recurrenceType === 'daily') {
		return {
			title,
			description: getTaskRoute(task) || null,
			workflow_description: task?.workflowDescription?.trim() || null,
			recurrence_type: 'daily',
			...assigneePayload
		};
	}

	if (task?.recurrenceType === 'weekly' && task?.weeklyDays?.length > 0) {
		return {
			title,
			description: getTaskRoute(task) || null,
			workflow_description: task?.workflowDescription?.trim() || null,
			...assigneePayload,
			recurrence_type: 'weekly_days',
			days_of_week: task.weeklyDays
				.map((day) => API_DAY_BY_LABEL[day])
				.filter(Boolean)
		};
	}

	if (task?.recurrenceType === 'custom' && isTaskConfigured(task)) {
		return {
			title,
			description: getTaskRoute(task) || null,
			workflow_description: task?.workflowDescription?.trim() || null,
			recurrence_type: 'interval',
			interval_value: Number(task.intervalValue),
			interval_unit: task.intervalUnit,
			recurrence_anchor_date: task.recurrenceAnchorDate,
			...assigneePayload
		};
	}

	return null;
};

const sortTasksByOrder = (tasks, templateOrderIds) => {
	const orderMap = new Map(templateOrderIds.map((id, index) => [String(id), index]));

	return [...tasks].sort((left, right) => {
		const leftOrder = left.templateId != null ? orderMap.get(String(left.templateId)) : undefined;
		const rightOrder = right.templateId != null ? orderMap.get(String(right.templateId)) : undefined;

		if (leftOrder != null && rightOrder != null && leftOrder !== rightOrder) {
			return leftOrder - rightOrder;
		}

		if (left.sortOrder !== right.sortOrder) {
			return left.sortOrder - right.sortOrder;
		}

		return left.label.localeCompare(right.label);
	});
};

const LoadingSpinner = ({ className = 'h-3.5 w-3.5 text-blue-600' }) => (
	<svg
		className={`animate-spin ${className}`}
		viewBox="0 0 24 24"
		fill="none"
		aria-hidden="true"
	>
		<circle
			cx="12"
			cy="12"
			r="9"
			stroke="currentColor"
			strokeOpacity="0.2"
			strokeWidth="3"
		/>
		<path
			d="M21 12a9 9 0 0 0-9-9"
			stroke="currentColor"
			strokeWidth="3"
			strokeLinecap="round"
		/>
	</svg>
);

const DailyRoutinePanel = ({ mode = 'panel' }) => {
	const dispatch = useCustomDispatch();
	const navigate = useCustomNavigate();
	const { loginUserID } = useCustomSelectors();
	const [isOpen, setIsOpen] = useState(false);
	const [query, setQuery] = useState('');
	const [debouncedQuery, setDebouncedQuery] = useState('');
	const [selectedRoute, setSelectedRoute] = useState(null);
	const [tasks, setTasks] = useState([]);
	const [pendingTask, setPendingTask] = useState(null);
	const [draftTasks, setDraftTasks] = useState([]);
	const [templateOrderIds, setTemplateOrderIds] = useState([]);
	const [editingTaskIds, setEditingTaskIds] = useState({});
	const [latestAddedTaskKey, setLatestAddedTaskKey] = useState('');
	const [confirmDeleteTaskId, setConfirmDeleteTaskId] = useState(null);
	const [isSuggestionsOpen, setIsSuggestionsOpen] = useState(false);
	const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(-1);
	const [suggestionsStyle, setSuggestionsStyle] = useState(null);
	const [isLoadingToday, setIsLoadingToday] = useState(false);
	const [isSharedFlowsOpen, setIsSharedFlowsOpen] = useState(false);
	const [sharedTemplates, setSharedTemplates] = useState([]);
	const [hasAvailableSharedFlows, setHasAvailableSharedFlows] = useState(false);
	const [selectedSharedTemplate, setSelectedSharedTemplate] = useState(null);
	const [isLoadingSharedFlows, setIsLoadingSharedFlows] = useState(false);
	const [isLoadingSharedTemplate, setIsLoadingSharedTemplate] = useState(false);
	const [sharedFlowsError, setSharedFlowsError] = useState('');
	const [importingTemplateId, setImportingTemplateId] = useState(null);
	const [todayError, setTodayError] = useState('');
	const [lookupError, setLookupError] = useState('');
	const [actionError, setActionError] = useState('');
	const [isLookupLoading, setIsLookupLoading] = useState(false);
	const [savingTaskIds, setSavingTaskIds] = useState({});
	const [deletingTaskIds, setDeletingTaskIds] = useState({});
	const [togglingTaskIds, setTogglingTaskIds] = useState({});
	const [reorderingTaskId, setReorderingTaskId] = useState(null);
	const [dragTaskId, setDragTaskId] = useState(null);
	const [dragOverTaskId, setDragOverTaskId] = useState(null);
	const [staffList, setStaffList] = useState([]);
	const [isLoadingStaff, setIsLoadingStaff] = useState(false);
	const [staffError, setStaffError] = useState('');
	const [isLoadingAssignees, setIsLoadingAssignees] = useState(false);
	const staffRequestedRef = useRef(false);
	// Template item id -> assignees ([{ id, name }]); run items don't include assignees.
	const templateAssigneesRef = useRef(new Map());
	// Template item id -> id of the user who created the shared step (they are never one of its assignees).
	const templateOwnersRef = useRef(new Map());
	const assigneeLookupAttemptedRef = useRef(new Set());
	const searchContainerRef = useRef(null);
	const searchInputRef = useRef(null);
	const togglePanel = useCallback(() => {
		setIsOpen((previous) => !previous);
	}, []);

	const routeOptions = useMemo(() => getDailyRoutineRouteOptions(), []);

	const setTaskPendingState = (setter, taskId, value) => {
		setter((previous) => {
			if (value) {
				return {
					...previous,
					[taskId]: true
				};
			}

			if (!(taskId in previous)) return previous;
			const next = { ...previous };
			delete next[taskId];
			return next;
		});
	};

	const resetSearch = () => {
		setQuery('');
		setDebouncedQuery('');
		setSelectedRoute(null);
		setIsSuggestionsOpen(false);
		setActiveSuggestionIndex(-1);
		setLookupError('');
	};

	useEffect(() => {
		if (!confirmDeleteTaskId) return undefined;

		const handleKeyDown = (event) => {
			if (event.key !== 'Escape') return;
			setConfirmDeleteTaskId(null);
		};

		window.addEventListener('keydown', handleKeyDown);
		return () => window.removeEventListener('keydown', handleKeyDown);
	}, [confirmDeleteTaskId]);

	const getTaskById = (taskId) => {
		if (pendingTask?.id === taskId) return pendingTask;
		const draftTask = draftTasks.find((task) => task.id === taskId);
		if (draftTask) return draftTask;
		return tasks.find((task) => task.id === taskId) || null;
	};

	const updateTask = (taskId, updater) => {
		setPendingTask((previous) => (previous?.id === taskId ? updater(previous) : previous));
		setDraftTasks((previousDrafts) =>
			previousDrafts.map((task) => (task.id === taskId ? updater(task) : task))
		);
		setTasks((previousTasks) =>
			previousTasks.map((task) => (task.id === taskId ? updater(task) : task))
		);
	};

	const preservePendingTask = useCallback(() => {
		if (!pendingTask) return;

		setDraftTasks((previousDrafts) => {
			if (previousDrafts.some((task) => task.id === pendingTask.id)) {
				return previousDrafts;
			}
			return [pendingTask, ...previousDrafts];
		});
		setPendingTask(null);
	}, [pendingTask]);

	const applyTodayRun = useCallback((response, orderIds = extractTemplateOrderIdsFromRun(response)) => {
		const run = extractTodayRun(response);
		const assigneeCache = templateAssigneesRef.current;
		const nextTasks = sortTasksByOrder(
			Array.isArray(run?.items)
				? run.items.map(mapRunItemToTask).map((task) => (
					task.assignees === null && task.templateId != null && assigneeCache.has(String(task.templateId))
						? { ...task, assignees: assigneeCache.get(String(task.templateId)) }
						: task
				))
				: [],
			orderIds
		);

		setTemplateOrderIds(orderIds);
		setTasks(nextTasks);
		setDraftTasks((previousDrafts) => previousDrafts.filter((draftTask) => (
			!nextTasks.some((task) => (
				normalizeDailyRoutineSearch(task.label) === normalizeDailyRoutineSearch(draftTask.label) &&
				getTaskRoute(task) === getTaskRoute(draftTask)
			))
		)));
		setEditingTaskIds((previous) =>
			Object.fromEntries(
				Object.entries(previous).filter(([taskId]) => nextTasks.some((task) => task.id === taskId))
			)
		);
	}, []);

	// `silent` refreshes in the background (e.g. a step was just assigned to this user) without the loading state.
	const refreshDailyRoutine = useCallback(async ({ silent = false } = {}) => {
		if (!silent) setIsLoadingToday(true);
		setTodayError('');

		try {
			const response = await dispatch(FetchDailyRoutineToday({ fetchType: 'dailyRoutineToday' }));
			applyTodayRun(response);
			setTodayError('');
		} catch (error) {
			if (!silent) setTodayError(getErrorMessage(error, 'Unable to load today\'s checklist.'));
		}

		if (!silent) setIsLoadingToday(false);
	}, [dispatch, applyTodayRun]);

	const loadStaff = useCallback(async () => {
		if (staffRequestedRef.current) return;
		staffRequestedRef.current = true;
		setIsLoadingStaff(true);
		setStaffError('');

		try {
			const response = await dispatch(FetchDailyRoutineStaff({ fetchType: 'dailyRoutineStaff' }));
			// Keep role ('pharmacist' | 'assistant') for the Pharmacists / Assistants tabs.
			setStaffList(extractStaff(response)
				.filter((member) => member?.id != null)
				.map((member) => ({ id: Number(member.id), name: member?.name || '', role: member?.role || 'assistant' })));
		} catch (error) {
			staffRequestedRef.current = false;
			setStaffError(getErrorMessage(error, 'Unable to load staff.'));
		} finally {
			setIsLoadingStaff(false);
		}
	}, [dispatch]);

	// Prefill assignees for existing steps opened in the editor from the template items.
	const loadTemplateAssignees = useCallback(async (templateIds) => {
		templateIds.forEach((id) => assigneeLookupAttemptedRef.current.add(String(id)));
		setIsLoadingAssignees(true);

		try {
			const response = await dispatch(FetchDailyRoutineTemplate({ fetchType: 'dailyRoutineTemplateAssignees' }));
			const template = extractTemplate(response);
			const assigneeCache = templateAssigneesRef.current;

			(template?.items || []).forEach((item) => {
				const assignees = normalizeAssignees(item?.assignees);
				if (item?.id != null && assignees) {
					assigneeCache.set(String(item.id), assignees);
				}
				if (item?.id != null && item?.owner_id != null) {
					templateOwnersRef.current.set(String(item.id), Number(item.owner_id));
				}
			});

			const fillAssignees = (task) => (
				task && task.assignees === null && task.templateId != null && assigneeCache.has(String(task.templateId))
					? { ...task, assignees: assigneeCache.get(String(task.templateId)) }
					: task
			);
			setTasks((previousTasks) => previousTasks.map(fillAssignees));
			setDraftTasks((previousDrafts) => previousDrafts.map(fillAssignees));
			setPendingTask((previous) => fillAssignees(previous));
		} catch (error) {
			// Leave assignees unknown; saving then keeps the existing assignee list untouched.
		} finally {
			setIsLoadingAssignees(false);
		}
	}, [dispatch]);

	const fetchSharedFlows = useCallback(async ({ showLoading = false } = {}) => {
		setSharedFlowsError('');
		if (showLoading) {
			setIsLoadingSharedFlows(true);
		}

		try {
			const response = await dispatch(FetchSharedDailyRoutineTemplates({
				fetchType: 'dailyRoutineSharedTemplates'
			}));
			const templates = extractSharedTemplates(response);
			setSharedTemplates(templates);
			setHasAvailableSharedFlows(templates.length > 0);
			setSelectedSharedTemplate(null);
			return templates;
		} catch (error) {
			setSharedFlowsError(getErrorMessage(error, 'Unable to load available workflows.'));
			setHasAvailableSharedFlows(false);
			return [];
		} finally {
			if (showLoading) {
				setIsLoadingSharedFlows(false);
			}
		}
	}, [dispatch]);

	const checkSharedFlowAvailability = useCallback(async () => {
		try {
			const response = await dispatch(FetchSharedDailyRoutineAvailability({
				fetchType: 'dailyRoutineSharedAvailability'
			}));
			setHasAvailableSharedFlows(extractSharedAvailability(response));
		} catch (error) {
			setHasAvailableSharedFlows(false);
		}
	}, [dispatch]);

	const openSharedFlows = async () => {
		setIsSharedFlowsOpen(true);
		const templates = await fetchSharedFlows({ showLoading: true });

		if (templates.length === 0) {
			setIsSharedFlowsOpen(false);
		}
	};

	const viewSharedTemplate = async (template) => {
		if (!template?.id) return;
		setSharedFlowsError('');
		setSelectedSharedTemplate(template);
		setIsLoadingSharedTemplate(true);

		try {
			const response = await dispatch(FetchSharedDailyRoutineTemplate({
				id: template.id,
				fetchType: `dailyRoutineSharedTemplate:${template.id}`
			}));
			const fullTemplate = extractTemplate(response);
			if (fullTemplate) {
				setSelectedSharedTemplate(fullTemplate);
				setSharedTemplates((previousTemplates) => previousTemplates.map((currentTemplate) => (
					String(currentTemplate.id) === String(fullTemplate.id) ? fullTemplate : currentTemplate
				)));
			}
		} catch (error) {
			setSharedFlowsError(getErrorMessage(error, 'Unable to load workflow details.'));
		} finally {
			setIsLoadingSharedTemplate(false);
		}
	};

	const importSharedTemplate = async (template) => {
		if (!template?.id) return;

		setImportingTemplateId(template.id);
		setSharedFlowsError('');
		setActionError('');

		try {
			const response = await dispatch(ImportSharedDailyRoutineTemplate({
				id: template.id,
				fetchType: `dailyRoutineSharedImport:${template.id}`
			}));
			applyTodayRun(response);
			toast.success('Workflow imported successfully.', { autoClose: 3000 });
			setIsSharedFlowsOpen(false);
			setSelectedSharedTemplate(null);
		} catch (error) {
			setSharedFlowsError(getErrorMessage(error, 'Unable to import workflow.'));
		} finally {
			setImportingTemplateId(null);
		}
	};

	useEffect(() => {
		const timer = setTimeout(() => {
			setDebouncedQuery(query.trim());
		}, 300);

		return () => clearTimeout(timer);
	}, [query]);

	useEffect(() => {
		const handleClickOutside = (event) => {
			if (!searchContainerRef.current?.contains(event.target)) {
				setIsSuggestionsOpen(false);
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, []);

	useEffect(() => {
		void refreshDailyRoutine();
	}, [refreshDailyRoutine]);

	// Someone assigned, changed or removed a step for this user (FlowAssignmentNotifier): append it without a reload.
	useEffect(() => {
		const handleFlowChanged = () => { void refreshDailyRoutine({ silent: true }); };
		window.addEventListener(FLOW_CHANGED_EVENT, handleFlowChanged);
		return () => window.removeEventListener(FLOW_CHANGED_EVENT, handleFlowChanged);
	}, [refreshDailyRoutine]);

	// Re-check every minute so "New" badges disappear once they are older than NEW_ASSIGNMENT_MS.
	const [clockTick, setClockTick] = useState(() => Date.now());
	useEffect(() => {
		const timer = window.setInterval(() => setClockTick(Date.now()), 60 * 1000);
		return () => window.clearInterval(timer);
	}, []);
	useEffect(() => {
		void checkSharedFlowAvailability();
	}, [checkSharedFlowAvailability]);

	useEffect(() => {
		if (mode === 'inline' || !isOpen) return;

		const focusTimer = window.setTimeout(() => {
			searchInputRef.current?.focus();
		}, 150);

		return () => window.clearTimeout(focusTimer);
	}, [isOpen, mode]);

	useEffect(() => {
		if (mode === 'inline' || !isOpen) return;

		const handleEscapeClose = (event) => {
			if (event.key !== 'Escape') return;
			setIsOpen(false);
		};

		window.addEventListener('keydown', handleEscapeClose);
		return () => window.removeEventListener('keydown', handleEscapeClose);
	}, [isOpen, mode]);

	useEffect(() => {
		if (mode === 'inline') return;

		const handleF2Toggle = (event) => {
			if (event.key !== 'F2') return;
			event.preventDefault();
			togglePanel();
		};

		window.addEventListener('keydown', handleF2Toggle);
		return () => window.removeEventListener('keydown', handleF2Toggle);
	}, [mode, togglePanel]);

	const customSuggestionOption = useMemo(() => {
		const trimmedQuery = debouncedQuery.trim();
		if (!trimmedQuery) return null;

		const normalizedQuery = normalizeDailyRoutineSearch(trimmedQuery);
		const hasExactRouteMatch = routeOptions.some((option) => (
			normalizeDailyRoutineSearch(option.label) === normalizedQuery ||
			normalizeDailyRoutineSearch(option.route) === normalizedQuery
		));

		if (hasExactRouteMatch) return null;

		return {
			label: trimmedQuery,
			route: '',
			searchText: normalizedQuery,
			isCustom: true
		};
	}, [debouncedQuery, routeOptions]);

	const suggestions = useMemo(() => {
		if (!debouncedQuery) return [];

		const normalizedQuery = normalizeDailyRoutineSearch(debouncedQuery);
		const matchingRouteOptions = routeOptions
			.filter((option) => option.searchText.includes(normalizedQuery))
			.slice(0, 8);

		if (!customSuggestionOption) {
			return matchingRouteOptions;
		}

		return [...matchingRouteOptions, customSuggestionOption];
	}, [customSuggestionOption, debouncedQuery, routeOptions]);

	useEffect(() => {
		if (mode !== 'inline') return;
		if (!isSuggestionsOpen || suggestions.length === 0) {
			setSuggestionsStyle(null);
			return;
		}

		const updateStyle = () => {
			const inputRect = searchInputRef.current?.getBoundingClientRect?.();
			if (!inputRect) return;
			setSuggestionsStyle({
				position: 'fixed',
				left: inputRect.left,
				top: inputRect.bottom + 6,
				width: inputRect.width,
				zIndex: 9999
			});
		};

		updateStyle();
		window.addEventListener('resize', updateStyle);
		window.addEventListener('scroll', updateStyle, true);

		return () => {
			window.removeEventListener('resize', updateStyle);
			window.removeEventListener('scroll', updateStyle, true);
		};
	}, [mode, isSuggestionsOpen, suggestions.length]);

	useEffect(() => {
		if (!isSuggestionsOpen || suggestions.length === 0) {
			setActiveSuggestionIndex(-1);
			return;
		}

		setActiveSuggestionIndex((previousIndex) => {
			if (previousIndex >= 0 && previousIndex < suggestions.length) {
				return previousIndex;
			}
			return 0;
		});
	}, [isSuggestionsOpen, suggestions]);

	const displayTasks = useMemo(() => {
		const baseTasks = [...draftTasks, ...tasks];
		if (!pendingTask) return baseTasks;
		return [pendingTask, ...baseTasks.filter((task) => task.id !== pendingTask.id)];
	}, [draftTasks, pendingTask, tasks]);
	const uncheckedTaskCount = useMemo(() => (
		displayTasks.reduce((count, task) => (
			task.isSelected ? count : count + 1
		), 0)
	), [displayTasks]);
	const { hasOpenEditor, missingAssigneeTemplateIdsKey } = useMemo(() => {
		const openEditorTasks = displayTasks.filter((task) => (
			Boolean(editingTaskIds[task.id] ?? (!task.isAssignedCopy && !isTaskConfigured(task)))
		));
		return {
			hasOpenEditor: openEditorTasks.length > 0,
			missingAssigneeTemplateIdsKey: openEditorTasks
				.filter((task) => task.templateId != null && task.assignees === null)
				.map((task) => String(task.templateId))
				.join(',')
		};
	}, [displayTasks, editingTaskIds]);

	useEffect(() => {
		if (!hasOpenEditor) return;
		void loadStaff();

		const missingIds = missingAssigneeTemplateIdsKey
			.split(',')
			.filter((id) => id && !assigneeLookupAttemptedRef.current.has(id));
		if (missingIds.length > 0) {
			void loadTemplateAssignees(missingIds);
		}
	}, [hasOpenEditor, missingAssigneeTemplateIdsKey, loadStaff, loadTemplateAssignees]);

	const visibleTemplateTasks = useMemo(() => (
		tasks.filter((task) => task.templateId != null)
	), [tasks]);
	const visibleTemplateIds = useMemo(() => (
		visibleTemplateTasks.map((task) => task.templateId)
	), [visibleTemplateTasks]);

	const resolveSelectedOption = () => {
		if (selectedRoute) return selectedRoute;

		const normalizedQuery = normalizeDailyRoutineSearch(query);
		return routeOptions.find((option) =>
			normalizeDailyRoutineSearch(option.label) === normalizedQuery ||
			normalizeDailyRoutineSearch(option.route) === normalizedQuery
		) || null;
	};

	const findExistingTask = (option, rawValue = '') => {
		const valueToMatch = rawValue.trim();

		if (option?.route) {
			const existingByRoute = displayTasks.find((item) => getTaskRoute(item) === option.route);
			if (existingByRoute) return existingByRoute;
		}

		if (!valueToMatch) return null;

		const normalizedValue = normalizeDailyRoutineSearch(valueToMatch);
		return displayTasks.find((item) =>
			normalizeDailyRoutineSearch(item.label) === normalizedValue ||
			normalizeDailyRoutineSearch(getTaskRoute(item)) === normalizedValue
		) || null;
	};

	const existingSearchTask = findExistingTask(selectedRoute, query);

	const openTaskEditor = (taskId) => {
		setActionError('');
		setEditingTaskIds((previous) => ({
			...previous,
			[taskId]: true
		}));
	};

	const closeTaskEditor = (taskId) => {
		setEditingTaskIds((previous) => ({
			...previous,
			[taskId]: false
		}));
	};

	const saveTask = async (task) => {
		const payload = buildTemplatePayload(task);
		if (!payload) return;

		setActionError('');
		setTaskPendingState(setSavingTaskIds, task.id, true);

		try {
			const todayInAppTimezone = new Intl.DateTimeFormat('en-US', {
				weekday: 'long',
				timeZone: APP_TIMEZONE
			}).format(new Date());
			const isWeeklyScheduledForLater = task?.recurrenceType === 'weekly' &&
				Array.isArray(task?.weeklyDays) &&
				task.weeklyDays.length > 0 &&
				!task.weeklyDays.includes(todayInAppTimezone);

			const response = task.templateId
				? await dispatch(UpdateDailyRoutineTemplateItem({
					id: task.templateId,
					payload: {
						...payload,
						is_active: true
					},
					fetchType: `dailyRoutineUpdate:${task.id}`
				}))
				: await dispatch(CreateDailyRoutineTemplateItem({
					payload,
					fetchType: `dailyRoutineCreate:${task.id}`
				}));

			const savedItem = extractToggleItem(response);
			const savedTemplateId = savedItem?.id ?? task.templateId;
			const savedAssignees = normalizeAssignees(savedItem?.assignees) ||
				(Array.isArray(task.assignees) ? task.assignees : null);
			if (savedTemplateId != null && savedAssignees) {
				templateAssigneesRef.current.set(String(savedTemplateId), savedAssignees);
			}
			// An assignee's save answers with the creator's step, so keep their own copy's list current too.
			if (task.templateId != null && savedAssignees) {
				templateAssigneesRef.current.set(String(task.templateId), savedAssignees);
			}

			applyTodayRun(response);
			if (isWeeklyScheduledForLater) {
				toast.success('Scheduled successfully — it will show on the assigned day.', { autoClose: 3000 });
			}
			setPendingTask((previous) => (previous?.id === task.id ? null : previous));
			setDraftTasks((previousDrafts) => previousDrafts.filter((draftTask) => draftTask.id !== task.id));
			closeTaskEditor(task.id);
		} catch (error) {
			setActionError(getErrorMessage(error, `Unable to save "${task.label}".`));
			openTaskEditor(task.id);
		} finally {
			setTaskPendingState(setSavingTaskIds, task.id, false);
		}
	};

	const handleTaskSelect = async (taskId) => {
		const task = getTaskById(taskId);
		if (!task?.runItemId) return;
		const taskRoute = getTaskRoute(task);

		const nextIsSelected = !task.isSelected;
		updateTask(taskId, (currentTask) => ({
			...currentTask,
			isSelected: nextIsSelected
		}));
		setActionError('');
		setTaskPendingState(setTogglingTaskIds, taskId, true);

		try {
			const response = await dispatch(ToggleDailyRoutineRunItem({
				id: task.runItemId,
				isCompleted: nextIsSelected,
				fetchType: `dailyRoutineToggle:${taskId}`
			}));
			const toggledItem = extractToggleItem(response);

			if (toggledItem) {
				updateTask(taskId, (currentTask) => ({
					...currentTask,
					isSelected: Boolean(toggledItem?.is_completed)
				}));
			}

			if (nextIsSelected && taskRoute) {
				if (mode !== 'inline') {
					setIsOpen(false);
				}
				navigate(taskRoute);
			}
		} catch (error) {
			updateTask(taskId, (currentTask) => ({
				...currentTask,
				isSelected: task.isSelected
			}));
			setActionError(getErrorMessage(error, `Unable to update "${task.label}".`));
		} finally {
			setTaskPendingState(setTogglingTaskIds, taskId, false);
		}
	};

	const handleRecurrenceChange = (taskId, recurrenceType) => {
		const currentTask = getTaskById(taskId);
		if (!currentTask) return;

		const nextTask = {
			...currentTask,
			recurrenceType,
			weeklyDays: recurrenceType === 'weekly' ? currentTask.weeklyDays : [],
			intervalValue: recurrenceType === 'custom' ? (currentTask.intervalValue || 1) : 1,
			intervalUnit: recurrenceType === 'custom' ? (currentTask.intervalUnit || 'day') : 'day',
			recurrenceAnchorDate: recurrenceType === 'custom'
				? (currentTask.recurrenceAnchorDate || getTodayInAppTimezone())
				: getTodayInAppTimezone()
		};

		updateTask(taskId, () => nextTask);
		openTaskEditor(taskId);
	};

	const handleWeeklyDayToggle = (taskId, day) => {
		const currentTask = getTaskById(taskId);
		if (!currentTask) return;

		const weeklyDays = currentTask.weeklyDays.includes(day)
			? currentTask.weeklyDays.filter((item) => item !== day)
			: [...currentTask.weeklyDays, day];
		const nextTask = {
			...currentTask,
			weeklyDays
		};

		updateTask(taskId, () => nextTask);
		openTaskEditor(taskId);
	};

	const handleAssigneesChange = (taskId, assignees) => {
		updateTask(taskId, (currentTask) => ({ ...currentTask, assignees: assignees || [] }));
		openTaskEditor(taskId);
	};

	const handleDeleteTask = async (taskId) => {
		const task = getTaskById(taskId);
		if (!task) return;

		if (!task.templateId) {
			if (pendingTask?.id === taskId) {
				setPendingTask(null);
				setLatestAddedTaskKey('');
			}
			setDraftTasks((previousDrafts) => previousDrafts.filter((draftTask) => draftTask.id !== taskId));
			setEditingTaskIds((previous) => {
				if (!(taskId in previous)) return previous;
				const next = { ...previous };
				delete next[taskId];
				return next;
			});
			return;
		}

		setActionError('');
		setTaskPendingState(setDeletingTaskIds, taskId, true);

		try {
			const response = await dispatch(DeleteDailyRoutineTemplateItem({
				id: task.templateId,
				fetchType: `dailyRoutineDelete:${taskId}`
			}));
			setTemplateOrderIds((previous) => previous.filter((id) => String(id) !== String(task.templateId)));
			applyTodayRun(response);
			setPendingTask((previous) => (
				previous?.id === taskId || previous?.templateId === task.templateId ? null : previous
			));
		} catch (error) {
			setActionError(getErrorMessage(error, `Unable to delete "${task.label}".`));
		} finally {
			setTaskPendingState(setDeletingTaskIds, taskId, false);
		}
	};

	const requestDeleteTask = (taskId) => {
		setConfirmDeleteTaskId((previous) => (previous === taskId ? null : taskId));
		openTaskEditor(taskId);
	};

	const confirmDeleteTask = (taskId) => {
		setConfirmDeleteTaskId(null);
		void handleDeleteTask(taskId);
	};

	const reorderTemplateItems = async (nextVisibleIds, taskId) => {
		if (!Array.isArray(nextVisibleIds) || nextVisibleIds.length === 0) return;
		if (templateOrderIds.length === 0) {
			setActionError('Template order is still loading. Please try reordering again.');
			return;
		}

		const visibleSet = new Set(visibleTemplateIds.map(String));
		let visiblePointer = 0;
		const nextTemplateOrderIds = templateOrderIds.map((id) => {
			if (!visibleSet.has(String(id))) return id;
			const replacement = nextVisibleIds[visiblePointer];
			visiblePointer += 1;
			return replacement;
		});

		setActionError('');
		setReorderingTaskId(taskId);

		try {
			const response = await dispatch(ReorderDailyRoutineTemplateItems({
				itemIds: nextTemplateOrderIds,
				fetchType: `dailyRoutineReorder:${taskId}`
			}));

			setTemplateOrderIds(nextTemplateOrderIds);
			applyTodayRun(response);
		} catch (error) {
			setActionError(getErrorMessage(error, 'Unable to reorder the checklist.'));
		} finally {
			setReorderingTaskId(null);
		}
	};

	const handleDragStart = (event, taskId) => {
		setActionError('');
		setDragTaskId(taskId);
		setDragOverTaskId(taskId);
		event.dataTransfer.effectAllowed = 'move';
		event.dataTransfer.setData('text/plain', taskId);
	};

	const handleDragOver = (event, taskId) => {
		if (!dragTaskId || dragTaskId === taskId) return;
		event.preventDefault();
		event.dataTransfer.dropEffect = 'move';
		if (dragOverTaskId !== taskId) {
			setDragOverTaskId(taskId);
		}
	};

	const resetDragState = () => {
		setDragTaskId(null);
		setDragOverTaskId(null);
	};

	const handleDrop = async (event, targetTaskId) => {
		event.preventDefault();

		const sourceTaskId = dragTaskId || event.dataTransfer.getData('text/plain');
		if (!sourceTaskId || sourceTaskId === targetTaskId) {
			resetDragState();
			return;
		}

		const sourceIndex = visibleTemplateTasks.findIndex((task) => task.id === sourceTaskId);
		const targetIndex = visibleTemplateTasks.findIndex((task) => task.id === targetTaskId);

		if (sourceIndex < 0 || targetIndex < 0) {
			resetDragState();
			return;
		}

		const nextVisibleTasks = [...visibleTemplateTasks];
		const [movedTask] = nextVisibleTasks.splice(sourceIndex, 1);
		nextVisibleTasks.splice(targetIndex, 0, movedTask);

		resetDragState();
		await reorderTemplateItems(
			nextVisibleTasks.map((task) => task.templateId),
			sourceTaskId
		);
	};

	const handleLookupSelection = async (option = resolveSelectedOption()) => {
		const label = (option?.label || query).trim();
		if (!label) return;

		setLookupError('');
		setActionError('');
		setIsLookupLoading(true);

		try {
			const response = await dispatch(LookupDailyRoutineTemplateItem({
				name: label,
				fetchType: 'dailyRoutineLookup'
			}));
			const lookupData = extractLookupData(response);
			const matchedVisibleTask = findExistingTask(option, label);

			if (lookupData?.exists && lookupData?.template_item) {
				const existingTask = matchedVisibleTask?.templateId === lookupData.template_item.id
					? matchedVisibleTask
					: tasks.find((task) => String(task.templateId) === String(lookupData.template_item.id));

				if (existingTask?.isAssignedCopy || (!existingTask && lookupData.template_item.is_assigned_copy)) {
					// Steps assigned by someone else can't be edited here.
					setActionError(getAssignedCopyMessage(existingTask || mapTemplateItemToTask(lookupData.template_item)));
				} else if (existingTask) {
					if (pendingTask && pendingTask.id !== existingTask.id) {
						preservePendingTask();
					}
					openTaskEditor(existingTask.id);
					setLatestAddedTaskKey('');
				} else {
					const editorTask = mapTemplateItemToTask(
						lookupData.template_item,
						lookupData.today,
						option?.route || ''
					);
					if (pendingTask && pendingTask.id !== editorTask.id) {
						preservePendingTask();
					}
					setPendingTask(editorTask);
					setLatestAddedTaskKey(getTaskHighlightKey(editorTask));
					openTaskEditor(editorTask.id);
				}
			} else {
				const draftTask = createDraftTask({
					title: label,
					route: option?.route || ''
				});
				if (pendingTask && pendingTask.id !== draftTask.id) {
					preservePendingTask();
				}
				setPendingTask(draftTask);
				setLatestAddedTaskKey(getTaskHighlightKey(draftTask));
				openTaskEditor(draftTask.id);
			}

			resetSearch();
		} catch (error) {
			setLookupError(getErrorMessage(error, 'Unable to check that routine item right now.'));
		} finally {
			setIsLookupLoading(false);
		}
	};

	const handleCustomizeExistingTask = () => {
		if (!existingSearchTask) return;
		if (existingSearchTask.isAssignedCopy) {
			setActionError(getAssignedCopyMessage(existingSearchTask));
			resetSearch();
			return;
		}
		openTaskEditor(existingSearchTask.id);
		resetSearch();
	};

	const searchField = (
		<div ref={searchContainerRef} className="relative">
			<div className="relative">
				<span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400">
					<svg
						xmlns="http://www.w3.org/2000/svg"
						viewBox="0 0 24 24"
						fill="none"
						stroke="currentColor"
						strokeWidth="2"
						strokeLinecap="round"
						strokeLinejoin="round"
						className="h-4 w-4"
						aria-hidden="true"
					>
						<circle cx="11" cy="11" r="8" />
						<path d="m21 21-4.35-4.35" />
					</svg>
				</span>
				<input
					ref={searchInputRef}
					type="text"
					value={query}
					disabled={isLoadingToday || isLookupLoading}
					onKeyDown={(event) => {
						if (event.key === 'ArrowDown') {
							if (suggestions.length === 0) return;
							event.preventDefault();
							setIsSuggestionsOpen(true);
							setActiveSuggestionIndex((previousIndex) => {
								if (previousIndex < 0) return 0;
								return (previousIndex + 1) % suggestions.length;
							});
							return;
						}

						if (event.key === 'ArrowUp') {
							if (suggestions.length === 0) return;
							event.preventDefault();
							setIsSuggestionsOpen(true);
							setActiveSuggestionIndex((previousIndex) => {
								if (previousIndex < 0) return suggestions.length - 1;
								return (previousIndex - 1 + suggestions.length) % suggestions.length;
							});
							return;
						}

						if (event.key === 'Escape') {
							setIsSuggestionsOpen(false);
							setActiveSuggestionIndex(-1);
							return;
						}

						if (event.key !== 'Enter') return;
						event.preventDefault();

						if (isSuggestionsOpen && suggestions.length > 0) {
							const highlightedOption = suggestions[
								activeSuggestionIndex >= 0 ? activeSuggestionIndex : 0
							];
							if (highlightedOption) {
								void handleLookupSelection(highlightedOption);
								return;
							}
						}

						if (existingSearchTask) {
							handleCustomizeExistingTask();
							return;
						}

						void handleLookupSelection();
					}}
					onFocus={() => {
						if (suggestions.length > 0) {
							setIsSuggestionsOpen(true);
						}
					}}
					onChange={(event) => {
						setQuery(event.target.value);
						setSelectedRoute(null);
						setIsSuggestionsOpen(true);
						setActiveSuggestionIndex(0);
						setLookupError('');
					}}
					placeholder="Search OATRx Flow"
					className="w-full py-2 pl-9 pr-3 text-sm rounded border border-gray-300 bg-gray-50 text-gray-900 placeholder-gray-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:bg-white transition-all disabled:cursor-not-allowed disabled:opacity-70"
				/>
			</div>

			{isSuggestionsOpen && suggestions.length > 0 && (
				<div
					className={`${mode === 'inline'
						? 'bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden'
						: 'absolute left-0 right-0 top-[40px] z-10 bg-white border border-gray-200 rounded-lg shadow-md overflow-hidden'
						}`}
					style={mode === 'inline' ? suggestionsStyle : undefined}
				>
					{suggestions.map((option, index) => {
						const existingTask = findExistingTask(option, option.label);
						const isActive = index === activeSuggestionIndex;

						return (
							<div
								key={option.route || `custom:${option.label}`}
								className={`flex items-center gap-3 px-3 py-2 transition-colors ${isActive ? 'bg-blue-50' : 'hover:bg-blue-50'}`}
							>
								<button
									type="button"
									onMouseEnter={() => setActiveSuggestionIndex(index)}
									onClick={() => {
										void handleLookupSelection(option);
									}}
									className="min-w-0 flex-1 text-left"
								>
									<div className="text-sm text-gray-700 hover:text-blue-700">
										{option.isCustom ? `Add "${option.label}"` : option.label}
									</div>
									{option.isCustom && (
										<div className="text-[11px] text-gray-400">Add custom item to My Workspace</div>
									)}
									{/* <div className="text-[11px] text-gray-400">{option.route}</div> */}
								</button>

								{existingTask && !existingTask.isAssignedCopy && (
									<button
										type="button"
										onClick={(event) => {
											event.stopPropagation();
											openTaskEditor(existingTask.id);
											resetSearch();
										}}
										className="shrink-0 inline-flex items-center justify-center px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-md transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
									>
										Customize
									</button>
								)}
							</div>
						);
					})}
				</div>
			)}
		</div>
	);

	const availableWorkflowsButton = hasAvailableSharedFlows ? (
		<button
			type="button"
			onClick={() => void openSharedFlows()}
			className="greenBadge inline-flex h-[38px] w-[42px] shrink-0 items-center justify-center rounded-md border border-green-300 hover:bg-green-100 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-1"
			title="Shared Workflows"
			aria-label="Shared Workflows"
		>
			<Share2 className="h-4 w-4" aria-hidden="true" />
		</button>
	) : null;

	const dailyContent = (
		<div className={mode === 'inline' ? 'p-0' : 'p-4'}>
			{mode === 'inline' && (
				<div className="mb-2 flex items-start gap-2">
					<div className="min-w-0 flex-1">
						{searchField}
					</div>
					{availableWorkflowsButton}
				</div>
			)}

			{(todayError || actionError || lookupError) && (
				<div className="mb-2 flex items-start justify-between gap-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
					<span>{todayError || actionError || lookupError}</span>
					{todayError && (
						<button
							type="button"
							onClick={() => void refreshDailyRoutine()}
							className="shrink-0 font-semibold text-red-700 hover:text-red-800"
						>
							Retry
						</button>
					)}
				</div>
			)}

			{(isLoadingToday || isLookupLoading) && (
				<div className="mb-2 inline-flex items-center gap-2 text-xs font-medium text-gray-600">
					<LoadingSpinner />
					{isLoadingToday ? 'Loading today\'s checklist...' : 'Checking for an existing template item...'}
				</div>
			)}

			<div className="space-y-2">
				{displayTasks.map((task) => {
					// Steps assigned by someone else are read-only here (can still be ticked and reordered).
					const isEditorOpen = Boolean(editingTaskIds[task.id] ?? (!task.isAssignedCopy && !isTaskConfigured(task)));
					const taskAssignees = Array.isArray(task.assignees) ? task.assignees : [];
					const isAssigneeListLoading = task.assignees === null && task.templateId != null && isLoadingAssignees;
					// On a step assigned to you, its creator is not someone you can assign it to. Only the
					// creator is left out: "Assigned by" can be another assignee who handed the step on, and
					// leaving them out of "All" would take the step off them.
					const stepOwnerId = task.isAssignedCopy
						? (templateOwnersRef.current.get(String(task.templateId)) ?? null)
						: null;
					const isSaving = Boolean(savingTaskIds[task.id]);
					const isDeleting = Boolean(deletingTaskIds[task.id]);
					const isToggling = Boolean(togglingTaskIds[task.id]);
					const isCheckboxDisabled = !task.runItemId || !isTaskConfigured(task) || isToggling || isSaving || isDeleting;
					const isDraggable = task.templateId != null && !pendingTask && !isSaving && !isDeleting && !reorderingTaskId;
					const isDropTarget = dragOverTaskId === task.id && dragTaskId !== task.id;
					const isLatestAdded = getTaskHighlightKey(task) === latestAddedTaskKey;

					return (
						<div
							key={task.id}
							draggable={isDraggable}
							onDragStart={(event) => handleDragStart(event, task.id)}
							onDragOver={(event) => {
								if (!isDraggable) return;
								handleDragOver(event, task.id);
							}}
							onDrop={(event) => {
								if (!isDraggable) return;
								void handleDrop(event, task.id);
							}}
							onDragEnd={resetDragState}
							className={`group rounded-lg border transition-colors ${isDropTarget
									? 'border-blue-400 bg-blue-50'
									: isLatestAdded
										? 'border-yellow-300 bg-[#fff9db]'
										: task.isSelected
											? 'border-blue-300 bg-blue-50/40'
											: 'border-gray-200 bg-white'
								} ${dragTaskId === task.id ? 'opacity-75' : ''} ${isDraggable ? 'cursor-grab active:cursor-grabbing' : ''}`}
						>
							<div className="flex items-center gap-2 px-1">
								<div className="flex-1 flex items-center gap-1 px-2 py-2 text-left">
									<label
										className={`flex h-5 w-5 shrink-0 items-center justify-center self-start ${isCheckboxDisabled ? 'cursor-not-allowed' : 'cursor-pointer'
											}`}
									>
										<input
											type="checkbox"
											checked={task.isSelected}
											disabled={isCheckboxDisabled}
											onChange={() => {
												void handleTaskSelect(task.id);
											}}
											className="peer sr-only rounded-full"
											aria-label={`Mark ${task.label} as done`}
										/>
										<span className={`flex h-5 w-5  items-center justify-center transition-colors ${task.isSelected
												? 'rounded-full bg-[#08c400] text-white'
												: 'rounded-full border border-gray-400 bg-white text-transparent'
											} ${isCheckboxDisabled ? 'opacity-60' : ''}`}>
											<svg
												width="18"
												height="18"
												viewBox="0 0 16 16"
												fill="none"
												aria-hidden="true"
											>
												<path
													d="M4.5 8.4L6.9 10.8L11.6 6.1"
													stroke="currentColor"
													strokeWidth="2.7"
													strokeLinecap="round"
													strokeLinejoin="round"
												/>
											</svg>
										</span>
									</label>
									<div className="min-w-0 flex-1 space-y-0.5 pl-2">
										<div className={`text-sm leading-tight ${task.isSelected ? 'text-gray-500 line-through' : 'text-gray-700'}`}>
											{task.label}
											{isNewAssignment(task, clockTick) && (
												<span className="ml-1.5 inline-flex items-center rounded-full bg-green-100 px-1.5 py-px align-middle text-[10px] font-semibold uppercase tracking-wide text-green-700 no-underline">
													New
												</span>
											)}
										</div>
										{task.workflowDescription && !isEditorOpen && (
											<div className="text-[11px] leading-snug text-gray-500">
												{task.workflowDescription}
											</div>
										)}
										{task.isAssignedCopy && task.assignedBy?.name && (
											<div className="text-[10px] leading-snug text-gray-400">
												Assigned by {capitalizeName(task.assignedBy.name)}
											</div>
										)}
										{task.isSelected && task.completedBy?.name && (
											<div className="text-[10px] leading-snug text-gray-400">
												Completed by{' '}
												<span className="font-medium text-gray-500">
													{task.completedBy.is_me ? 'You' : capitalizeName(task.completedBy.name)}
												</span>
												{task.completedAtLabel && ` (${task.completedAtLabel})`}
											</div>
										)}
										{/* <div className="inline-flex items-center gap-1.5  text-[11px] leading-tight text-gray-400">
											{(savingTaskIds[task.id] || deletingTaskIds[task.id] || togglingTaskIds[task.id] || reorderingTaskId === task.id) && (
												<LoadingSpinner className="h-3 w-3" />
											)}
											<span className="-mt-[4px]">{renderTaskStatus(task)}</span>
										</div> */}
									</div>
								</div>

								<div className="shrink-0 flex items-center gap-1 pr-1 opacity-0 translate-x-1 pointer-events-none transition-all duration-200 group-hover:opacity-100 group-hover:translate-x-0 group-hover:pointer-events-auto group-focus-within:opacity-100 group-focus-within:translate-x-0 group-focus-within:pointer-events-auto">
									<button
										type="button"
										onClick={(event) => {
											event.stopPropagation();
											openTaskEditor(task.id);
										}}
										disabled={isSaving || isDeleting}
										className="p-2 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
										aria-label={`Edit ${task.label}`}
										title="Edit"
									>
										<svg
											xmlns="http://www.w3.org/2000/svg"
											width="24"
											height="24"
											viewBox="0 0 24 24"
											fill="none"
											stroke="currentColor"
											strokeWidth="2"
											strokeLinecap="round"
											strokeLinejoin="round"
											className="w-4 h-4"
											aria-hidden="true"
										>
											<path d="M12 20h9" />
											<path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5Z" />
										</svg>
									</button>
									<button
										type="button"
										onClick={(event) => {
											event.stopPropagation();
											requestDeleteTask(task.id);
										}}
										disabled={isSaving || isDeleting}
										className={`p-2 rounded-md text-red-600 hover:text-red-700 hover:bg-red-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-1 ${confirmDeleteTaskId === task.id ? 'ring-2 ring-red-200 bg-red-50/50' : ''
											}`}
										aria-label={`Delete ${task.label}`}
										title="Delete"
									>
										<svg
											xmlns="http://www.w3.org/2000/svg"
											width="24"
											height="24"
											viewBox="0 0 24 24"
											fill="none"
											stroke="currentColor"
											strokeWidth="2"
											strokeLinecap="round"
											strokeLinejoin="round"
											className="w-4 h-4"
											aria-hidden="true"
										>
											<path d="M3 6h18" />
											<path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
											<path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
											<line x1="10" x2="10" y1="11" y2="17" />
											<line x1="14" x2="14" y1="11" y2="17" />
										</svg>
									</button>
								</div>
							</div>

							<div className={`overflow-hidden transition-all duration-300 ease-in-out ${isEditorOpen ? 'max-h-[760px] opacity-100' : 'max-h-0 opacity-0'}`}>
								<div className="px-3 pb-2 pt-2">
									<div className="flex flex-col gap-3">
										<div className="min-w-0">
											{/* <div className="mb-1 text-[10px] font-medium uppercase tracking-wide text-gray-500">
												Recurrence Type
											</div> */}
											<div className="flex gap-1.5">
												{['daily', 'weekly', 'custom'].map((recurrenceType) => (
													<button
														key={recurrenceType}
														type="button"
														disabled={isSaving || isDeleting}
														onClick={() => handleRecurrenceChange(task.id, recurrenceType)}
														className={`inline-flex items-center justify-center h-7 rounded-md px-2.5 text-xs font-semibold capitalize transition-colors ${task.recurrenceType === recurrenceType
																? 'bg-blue-100 text-blue-800 border border-blue-300'
																: 'bg-white text-gray-600 border border-gray-300 hover:border-blue-300 hover:text-blue-700'
															} ${isSaving || isDeleting ? 'opacity-60 cursor-not-allowed' : ''}`}
													>
														{recurrenceType}
													</button>
												))}
											</div>


										</div>

										{task.recurrenceType === 'weekly' && (
											<div className="min-w-0">
												{/* <div className="mb-1 text-[10px] font-medium uppercase tracking-wide text-gray-500">
													Select Days
												</div> */}
												<div className="flex flex-nowrap items-center gap-1.5 overflow-x-auto pb-0.5">
													{WEEK_DAYS.map((day) => {
														const isSelected = task.weeklyDays.includes(day);
														return (
															<button
																key={day}
																type="button"
																disabled={isSaving || isDeleting}
																onClick={() => handleWeeklyDayToggle(task.id, day)}
																className={`flex-none inline-flex items-center justify-center h-7 w-7 rounded-md text-xs font-semibold transition-colors ${isSelected
																		? 'bg-blue-100 text-blue-800 border border-blue-300'
																		: 'border border-gray-300 bg-white text-gray-600 hover:border-blue-300 hover:text-blue-700'
																	} ${isSaving || isDeleting ? 'opacity-60 cursor-not-allowed' : ''}`}
																aria-pressed={isSelected}
																title={day}
															>
																{WEEK_DAY_SHORT[day] || day.slice(0, 1)}
															</button>
														);
													})}
												</div>
											</div>
										)}

										{task.recurrenceType === 'custom' && (
											<div className="min-w-0 w-full">
												<div className="flex flex-nowrap items-center gap-2 overflow-x-auto pb-1">
													<div className="flex items-center gap-2">
														<span className="text-xs font-medium text-gray-600">Every</span>
														<input
															type="number"
															min="1"
															max="365"
															value={task.intervalValue ?? 1}
															disabled={isSaving || isDeleting}
															onChange={(event) => {
																const value = event.target.value;
																updateTask(task.id, (currentTask) => ({ ...currentTask, intervalValue: value }));
															}}
															aria-label="Custom recurrence interval"
														className="h-9 w-16 flex-none rounded-md border border-gray-300 px-3 text-sm leading-normal text-gray-700 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 disabled:opacity-60"
														/>
													</div>
													<select
														value={task.intervalUnit || 'day'}
														disabled={isSaving || isDeleting}
														onChange={(event) => {
															const value = event.target.value;
															updateTask(task.id, (currentTask) => ({ ...currentTask, intervalUnit: value }));
														}}
														aria-label="Custom recurrence unit"
														className="h-9 w-32 flex-none rounded-md border border-gray-300 bg-white px-3 text-sm leading-normal text-gray-700 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 disabled:opacity-60"
													>
														<option value="day">day(s)</option>
														<option value="week">week(s)</option>
														<option value="month">month(s)</option>
													</select>
													<div className="w-32 flex-none">
														<DailyRoutineDatePicker
															value={task.recurrenceAnchorDate}
															disabled={isSaving || isDeleting}
															onChange={(value) => {
																updateTask(task.id, (currentTask) => ({ ...currentTask, recurrenceAnchorDate: value }));
															}}
														/>
													</div>
												</div>
											</div>
										)}
									</div>

									<div className="mt-3">
										<textarea
											value={task.workflowDescription || ''}
											disabled={isSaving || isDeleting}
											onChange={(event) => {
												const nextValue = event.target.value;
												updateTask(task.id, (currentTask) => ({
													...currentTask,
													workflowDescription: nextValue
												}));
											}}
											rows={3}
											placeholder="Add instructions for this workflow step"
											className="w-full resize-none rounded-md border border-gray-300 px-2.5 py-2 text-xs leading-relaxed text-gray-700 placeholder-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-60"
										/>
										<div className="mt-2">
											{/* Assignees can hand the step on too; they stay on it themselves (lockedUserId). */}
											<FlowAssigneePicker
												staff={staffList}
												value={taskAssignees}
												onChange={(assignees) => handleAssigneesChange(task.id, assignees)}
												disabled={isSaving || isDeleting}
												loading={isLoadingStaff || isAssigneeListLoading}
												lockedUserId={task.isAssignedCopy ? loginUserID : null}
												excludedUserId={stepOwnerId}
											/>
											{staffError && (
												<div className="mt-1 text-[11px] text-red-600">{staffError}</div>
											)}
										</div>
										<div className="mt-2 flex flex-wrap items-center gap-2">
											<button
												type="button"
												onClick={() => void saveTask(task)}
												disabled={isSaving || isDeleting || !isTaskConfigured(task)}
												className="inline-flex h-8 items-center justify-center rounded-md bg-blue-600 px-3 text-xs font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
											>
												{isSaving ? 'Saving' : 'Continue'}
											</button>
											{!isTaskConfigured(task) && (
												<span className="text-[11px] text-gray-500">
													Choose a valid daily, weekly, or custom schedule to continue.
												</span>
											)}
										</div>
									</div>

										<div
											className={`overflow-hidden transition-all duration-300 ease-in-out ${confirmDeleteTaskId === task.id
													? 'max-h-24 opacity-100 mt-2'
													: 'max-h-0 opacity-0 mt-0 pointer-events-none'
												}`}
										>
											<div className="flex flex-wrap items-center gap-2 rounded-md border border-red-200 bg-red-50 px-2.5 py-2 text-xs">
												<span className="min-w-0 flex-1 font-medium text-red-700 break-words whitespace-normal">
													Are you sure you want to delete "{task.label}"?
												</span>
												<div className="flex items-center gap-2">
													<button
														type="button"
														onClick={() => setConfirmDeleteTaskId(null)}
														disabled={isSaving || isDeleting}
														className="px-2 py-1 rounded-md text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-1"
													>
														Cancel
													</button>
													<button
														type="button"
														onClick={() => confirmDeleteTask(task.id)}
														disabled={isSaving || isDeleting}
														className="px-2 py-1 rounded-md text-white bg-red-600 hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-1"
													>
														Delete
													</button>
												</div>
											</div>
										</div>
								</div>
							</div>
						</div>
					);
				})}

				{!isLoadingToday && displayTasks.length === 0 && (
					<div className="px-0 py-0">
						<p className="text-sm font-medium text-gray-700">Shape your daily OATRx routine</p>
						<p className=" text-xs text-gray-500">
							Search above to add the steps, duties, and workflows you use throughout the day.
						</p>
					</div>
				)}
			</div>

			{isSharedFlowsOpen && createPortal((
				<div className="fixed inset-0 z-[10000] flex items-start justify-center overflow-y-auto bg-black/40 p-2 sm:p-4">
					<div className="flex h-[calc(100vh-32px)] w-full max-w-7xl flex-col overflow-hidden rounded-lg bg-white shadow-2xl">
						<div className="flex shrink-0 items-center justify-between gap-3 border-b border-gray-200 px-4 py-2.5">
							<div className="min-w-0">
								<h3 className="truncate text-[13px] font-semibold text-gray-900">Available Workflows</h3>
								<p className="text-[11px] text-gray-500">View and import workflows created by other users.</p>
							</div>
							<div className="flex shrink-0 items-center gap-2">
								{selectedSharedTemplate ? (
									<button
										type="button"
										onClick={() => void importSharedTemplate(selectedSharedTemplate)}
										disabled={String(importingTemplateId) === String(selectedSharedTemplate.id)}
										className="inline-flex h-8 items-center justify-center rounded-md bg-blue-600 px-3 text-xs font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
									>
										{String(importingTemplateId) === String(selectedSharedTemplate.id) ? 'Importing' : 'Import'}
									</button>
								) : null}
								<button
									type="button"
									onClick={() => {
										setIsSharedFlowsOpen(false);
										setSelectedSharedTemplate(null);
									}}
									className="inline-flex h-8 w-8 items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
									aria-label="Close available workflows"
								>
								<svg
									xmlns="http://www.w3.org/2000/svg"
									width="18"
									height="18"
									viewBox="0 0 24 24"
									fill="none"
									stroke="currentColor"
									strokeWidth="2"
									strokeLinecap="round"
									strokeLinejoin="round"
									aria-hidden="true"
								>
									<path d="M18 6 6 18" />
									<path d="m6 6 12 12" />
								</svg>
								</button>
							</div>
						</div>

						<div className="grid min-h-0 flex-1 grid-cols-1 overflow-hidden md:grid-cols-[minmax(300px,0.75fr)_minmax(0,1.25fr)]">
							<div className="min-h-0 border-b border-gray-200 p-2.5 md:border-b-0 md:border-r">
								{sharedFlowsError && (
									<div className="mb-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
										{sharedFlowsError}
									</div>
								)}

								{isLoadingSharedFlows && (
									<div className="inline-flex items-center gap-2 text-xs font-medium text-gray-600">
										<LoadingSpinner />
										Loading workflows...
									</div>
								)}

								{!isLoadingSharedFlows && sharedTemplates.length === 0 && (
									<div className="rounded-md border border-gray-200 px-3 py-4 text-sm text-gray-600">
										No shared workflows are available.
									</div>
								)}

								<div className="h-full max-h-full space-y-1.5 overflow-y-auto pr-1">
									{sharedTemplates.map((template) => {
										const isSelected = String(selectedSharedTemplate?.id) === String(template.id);

										return (
											<button
												key={template.id}
												type="button"
												onClick={() => void viewSharedTemplate(template)}
												className={`w-full rounded-md border border-gray-200 p-2.5 text-left transition-colors focus:outline-none ${isSelected ? 'bg-blue-50/60' : 'bg-white hover:bg-gray-50'}`}
												aria-label={`View ${getSharedFlowLabel(template)}`}
											>
												<div className="truncate text-[13px] font-semibold leading-tight text-gray-800">
													{getSharedFlowLabel(template)}
												</div>
											</button>
										);
									})}
								</div>
							</div>

							<div className="flex min-h-0 flex-col overflow-hidden p-3">
								{selectedSharedTemplate ? (
									<>
										{isLoadingSharedTemplate ? (
											<div className="flex min-h-0 flex-1 items-center justify-center rounded-md border border-dashed border-gray-300 text-xs font-medium text-gray-500">
												<LoadingSpinner className="mr-2 h-3.5 w-3.5 text-blue-600" />
												Loading workflow...
											</div>
										) : (
											<div className="min-h-0 flex-1 space-y-1.5 overflow-y-auto pr-1">
												{(selectedSharedTemplate.items || []).map((item, index) => (
													<div key={item.id || `${item.title}-${index}`} className="rounded-md border border-gray-200 bg-white px-2.5 py-2">
														<div className="flex items-center gap-1.5">
															<span className="mt-0.5 inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-blue-50 px-1.5 text-[10px] font-semibold text-blue-700">
																{index + 1}
															</span>
															<div className="min-w-0 flex-1">
																<div className="text-[13px] font-semibold leading-tight text-gray-800">
																	{item.title}
																</div>
																{item.workflow_description ? (
																	<div className="mt-0.5 text-[11px] leading-snug text-gray-600">
																		{item.workflow_description}
																	</div>
																) : null}
															</div>
														</div>
													</div>
												))}
											</div>
										)}
									</>
								) : (
									<div className="flex min-h-0 flex-1 items-center justify-center rounded-md border border-dashed border-gray-300 px-4 text-center text-sm text-gray-500">
										Select a workflow to view its steps and descriptions.
									</div>
								)}
							</div>
						</div>
					</div>
				</div>
			), document.body)}
		</div>
	);

	if (mode === 'inline') {
		return <div className="bg-white">{dailyContent}</div>;
	}

	return (
		<>
			<button
				type="button"
				onClick={togglePanel}
				className="fixed right-0 top-1/2 -translate-y-1/2 z-[60] inline-flex items-center justify-center w-9 h-32 text-white bg-blue-600 hover:bg-blue-700 rounded-l-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 shadow-sm"
				aria-label={isOpen ? 'Close daily routine panel' : 'Open daily routine panel'}
			>
				<span className="-rotate-90 inline-flex items-center gap-1 whitespace-nowrap text-[12px] font-semibold tracking-[0.18em]">
					<span>OATRx Flow</span>
					{uncheckedTaskCount > 0 && (
						<span className="inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-white px-1 text-[9px] font-bold leading-none tracking-normal text-blue-600">
							{uncheckedTaskCount}
						</span>
					)}
				</span>
			</button>

			<div className={`fixed inset-0 z-[70] ${isOpen ? 'pointer-events-auto' : 'pointer-events-none'}`}>
				<div
					className={`absolute inset-0 bg-black/30 transition-opacity duration-300 ${isOpen ? 'opacity-100' : 'opacity-0'}`}
					onClick={() => setIsOpen(false)}
				/>

				{/* Header stays fixed; the step list scrolls below it. */}
				<div className={`absolute right-0 top-0 flex h-full w-full max-w-md flex-col bg-white shadow-2xl border-l border-gray-200 transition-transform duration-300 ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}>
					<div className="flex shrink-0 items-center gap-3 px-4 py-3 border-b border-gray-200 bg-white">
						<div className="min-w-0 flex-1">
							{searchField}
						</div>
						{availableWorkflowsButton}
						<button
							type="button"
							onClick={togglePanel}
							className="inline-flex h-[38px] min-w-[42px] items-center justify-center rounded-md border border-blue-200 bg-blue-50 px-2.5 text-xs font-semibold text-blue-700 transition-colors hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
							aria-label={isOpen ? 'Close panel with F2' : 'Open panel with F2'}
							title={isOpen ? 'Close panel with F2' : 'Open panel with F2'}
						>
							F2
						</button>
					</div>

					<div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
						{dailyContent}
					</div>
				</div>
			</div>
		</>
	);
};

export default DailyRoutinePanel;
